import { useEffect, useState } from "react";
import { api, eur, API_BASE } from "@/lib/rentApi";
import { PageHeader, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const YEARS = [0, 1, 2].map((i) => new Date().getFullYear() - i);

export default function TaxReturn() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);

  const load = (y) => { setData(null); api.get("/tax/summary", { params: { year: y } }).then((r) => setData(r.data)); };
  useEffect(() => { load(year); }, [year]);

  const download = async () => {
    try {
      const token = localStorage.getItem("rent_token");
      const res = await fetch(`${API_BASE}/tax/liasse/${year}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `liasse_2031_${year}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Liasse fiscale téléchargée");
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div>
      <PageHeader
        title="Liasse fiscale 2031"
        subtitle="Régime réel LMNP / LMP — résultat fiscal et amortissements."
        action={
          <div className="flex gap-2 items-center">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28" data-testid="tax-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="rounded-full gap-2" onClick={download} data-testid="download-liasse-btn"><Download className="w-4 h-4" /> Télécharger PDF</Button>
          </div>
        }
      />

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Metric label="Recettes" value={eur(data.total_revenues)} />
            <Metric label="Charges" value={eur(data.total_expenses)} />
            <Metric label="Amortissements" value={eur(data.total_amortization)} />
            <Metric label="Résultat fiscal" value={eur(data.result)} highlight={data.result < 0} testid="tax-result" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Dotations aux amortissements" icon={FileSpreadsheet}>
              {data.amortizations.length === 0 ? <p className="text-sm text-muted-foreground p-4">Renseignez la valeur d'acquisition de vos biens pour calculer les amortissements.</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Bien</TableHead><TableHead>Base</TableHead><TableHead className="text-right">Dotation /an</TableHead></TableRow></TableHeader>
                  <TableBody>{data.amortizations.map((a, i) => (
                    <TableRow key={i}><TableCell>{a.label}</TableCell><TableCell className="font-mono-plex">{eur(a.base)}</TableCell><TableCell className="text-right font-mono-plex">{eur(a.annual)}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </Section>
            <Section title="Détail recettes / charges">
              <div className="p-4 space-y-2 text-sm">
                {Object.entries(data.revenues_by_cat).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-[hsl(var(--chart-2))]">{k}</span><span className="font-mono-plex">+{eur(v)}</span></div>
                ))}
                {Object.entries(data.expenses_by_cat).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-destructive">{k}</span><span className="font-mono-plex">−{eur(v)}</span></div>
                ))}
              </div>
            </Section>
          </div>

          <p className="text-xs text-muted-foreground mt-6 max-w-3xl">
            Document simplifié généré à partir de votre comptabilité. La transmission officielle au fisc s'effectue via un partenaire EDI-TDFC agréé — cette connexion pourra être activée ultérieurement.
          </p>
        </>
      )}
    </div>
  );
}

const Metric = ({ label, value, highlight, testid }) => (
  <div className="p-5 rounded-xl border border-border bg-card" data-testid={testid}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl font-bold mt-2 ${highlight ? "text-destructive" : ""}`}>{value}</p>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="px-5 py-4 border-b border-border flex items-center gap-2"><h3 className="font-heading font-semibold">{title}</h3></div>
    {children}
  </div>
);
